// @flow
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { MdDirections, MdDelete } from 'react-icons/lib/md';
import moment from 'moment';
import _ from 'lodash';
import i18n from 'i18n-js';

import type { ROUTE_TYPE } from '../../types/routes';
import * as selectors from '../../reducers';
import * as routeActions from '../../actions/routes';
import * as closeableActions from '../../actions/closeable';
import { FullLoader } from '../Loader';
import ConfirmDeletionPopup from '../ConfirmDeletionPopup';
import DataTable from '../DataTable';
import HighlightColumn from '../DataTable/Columns/Highlight';
import UserColumn from '../DataTable/Columns/User';
import ButtonColumn from '../DataTable/Columns/Button';
import SelectColumn from '../DataTable/Columns/Select';
import TimeColumn from '../DataTable/Columns/Time';
import PlaceholderView from '../PlaceholderViews';
import PlaceholderImage from '../../../assets/images/routesph.png';
import Popup from '../Popup';
import {
  Loading,
  NoResults,
} from '../CommonTexts';

import styles from './routesListCard.css';
import { getUserImage } from '../../utils';


type RoutesListCardPropTypes = {
  onSearch: Function,
  open: Function,
  routes: Array<ROUTE_TYPE>,
  onRouteClick: Function,
  isFetchingRoutes: boolean,
  editRoute: Function,
  buttonSelectRoute: Function,
  routeToDelete: ROUTE_TYPE,
  popupAction: Function,
};

class RoutesListCard extends React.Component <RoutesListCardPropTypes> {
  componentWillMount() {
    const { onSearch } = this.props;

    // Load initial values
    onSearch();
  }

  render() {
    const {
      routes,
      open,
      onRouteClick,
      isFetchingRoutes,
      editRoute,
      buttonSelectRoute,
      routeToDelete,
      popupAction,
    } = this.props;
    return (
      <Fragment>
        {
          !isFetchingRoutes
          && routes.length === 0
          && <PlaceholderView
              size="large"
              image={PlaceholderImage}
              title={NoResults(i18n.t('common.routesOrStops').toLowerCase())}
              subtitle={i18n.t('common.noCoincidences')}/>
        }
        {
          isFetchingRoutes && (
            <FullLoader
              tint="main"
              title={Loading(i18n.t('labels.route', { count: 2 }))}
            />
          )
        }
        {
          !isFetchingRoutes
          && routes.length > 0
          && (
            <Fragment>
              <Popup
                id="confirmDeleteRoute"
                theme="main"
                transitionType="slideDown"
                transitionMs={600}
              >
                {routeToDelete // La current ruta ya salio del estado inicial?
                  ? (<ConfirmDeletionPopup
                    onConfirm={() => popupAction()}
                    Icon={MdDelete}
                    descriptionText={
                      `¿Esta seguro de querer borrar la ruta ${routeToDelete.name}?
                      Se borraran todas las paradas asociadas y las asignaciones con los estudiantes.`
                    }
                  />) : (<Fragment />) }
              </Popup>
              <DataTable
                defaultPageSize={100}
                data={routes}
                columns={
                  [
                    HighlightColumn({
                      Header: i18n.t('labels.route', { count: 1 }),
                      accessor: 'short_name',
                      editable: true,
                      onChange: (a, b) => editRoute({ ...a, short_name: b }),
                      getOnClick: (params: { id: number }) => () => {
                        onRouteClick(params.id);
                      },
                      maxWidth: 210,
                      minWidth: 130,
                    }),
                    UserColumn({
                      Header: 'Monitor',
                      accessor: 'monitor',
                      editable: true,
                      minWidth: 200,
                      onChange: (a, b) => editRoute({ ...a, monitor: b.id, driver: a.driver.id }),
                    }),
                    UserColumn({
                      Header: i18n.t('labels.driver'),
                      accessor: 'driver',
                      editable: true,
                      minWidth: 200,
                      onChange: (a, b) => editRoute({ ...a, driver: b.id, monitor: a.monitor.id }),
                    }),
                    SelectColumn({
                      Header: i18n.t('labels.type'),
                      accessor: 'typeValue',
                      options: [
                        { value: 'going', label: 'ida' },
                        { value: 'returning', label: 'regreso' },
                      ],
                      editable: true,
                      minWidth: 180,
                      style: { paddingRight: 10 },
                      onChange: (a, b) => editRoute({ id: a.id, route_type: b }),
                    }),
                    TimeColumn({
                      Header: i18n.t('routes.expectedDepartureTime'),
                      accessor: 'localDepTime',
                      editable: true,
                      maxWidth: 180,
                      onChange: (a, b) => editRoute({ id: a.id, expected_departure_time: b }),
                    }),
                    TimeColumn({
                      Header: i18n.t('routes.expectedArrivalTime'),
                      accessor: 'localCompTime',
                      editable: true,
                      maxWidth: 180,
                      onChange: (a, b) => editRoute({ id: a.id, expected_completion_time: b }),
                    }),
                    // ButtonColumn({
                    //   Header: i18n.t('labels.stops'),
                    //   accessor: 'actionProps',
                    //   getOnClick: (params: { id: number }) => () => {
                    //     onRouteClick(params.id);
                    //   },
                    //   buttonProps: {
                    //     size: 'small',
                    //     tint: 'secondary',
                    //     Icon: MdDirections,
                    //     title: i18n.t('labels.stops'),
                    //   },
                    // }),
                    ButtonColumn({
                      accessor: 'actionProps',
                      getOnClick: (params: { id: number }) => () => {
                        buttonSelectRoute(params.id);
                        open('confirmDeleteRoute');
                      },
                      buttonProps: {
                        Icon: MdDelete,
                        isOutlined: true,
                        isTransparent: true,
                        size: 'medium ',
                        tint: 'error',
                      },
                      width: 50,
                    }),
                  ]
                }
                minRows={0}
                showPagination={false}
              />
            </Fragment>
          )
        }
      </Fragment>
    );
  }
}

export default connect(
  (state, { id }) => {
    const profile = selectors.getProfileOfType(state, 'admin');
    const routes = selectors.getVisibleRoutes(state);
    routes.forEach((route) => {
      const { expected_departure_time, expected_completion_time } = route;
      const localDepTime = moment.utc(expected_departure_time, 'HH: mm').local().format('HH: mm A');
      const localCompTime = moment.utc(expected_completion_time, 'HH: mm').local().format('HH: mm A');
      route.typeValue = {
        value: route.route_type,
      };
      route.monitor = {
        ...route.monitor,
        firstName: route.monitor ? route.monitor.first_name : 'N/A',
        lastName: route.monitor ? route.monitor.last_name : 'N/A',
        pictureURL:  route.monitor ? getUserImage(route.monitor.profile_picture) : 'N/A',
        userRole: 'monitor',
      };
      route.localDepTime = localDepTime;
      route.localCompTime = localCompTime;
      route.driver = {
        ...route.driver,
        firstName: route.driver ? route.driver.first_name : 'N/A',
        lastName: route.driver ? route.driver.last_name : 'N/A',
        pictureURL: route.driver ? getUserImage(route.driver.profile_picture) : 'N/A',
        userRole: 'driver',
      };
    });
    return {
      id,
      isShowingStopsDetail: selectors.isRouteWatchingStops(state, id),
      isFetchingRoutes: selectors.isFetchingRoutes(state),
      routes,
      currentRoute: selectors.getCurrentRoute(state),
      routeToDelete: selectors.getRouteToDelete(state),
      schoolId: profile && profile.school ? profile.school : -1,
    };
  },
  dispatch => ({
    onSearch(schoolId: number, text?: string) {
      dispatch(routeActions.startFetchSchoolRoutes(schoolId, text));
    },
    onRouteClick(id: number) {
      dispatch(push(`/admin/map/route/${id}`));
    },
    editRoute(new_route: ROUTE_TYPE) {
      dispatch(routeActions.updateRouteStarted(new_route));
    },
    buttonSelectRoute(routeId: number) {
      dispatch(routeActions.setRouteToDelete(routeId));
    },
    removeRoute(id, popupID) {
      dispatch(routeActions.deleteRouteStarted(id));
      dispatch(closeableActions.close(popupID));
    },
    open: (id: string) => dispatch(closeableActions.open(id)),
  }),
  (
    { schoolId, routeToDelete, ...stateProps },
    { onSearch, removeRoute, ...dispatchProps },
    ownProps,
  ) => ({
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    routeToDelete,
    popupAction: () => removeRoute(routeToDelete.id, 'confirmDeleteRoute'),
    onSearch(text: string) {
      onSearch(schoolId, text);
    },
  }),
  {
    areStatesEqual(next, prev) {
      return _.isEqual(next.routes, prev.routes);
    },
  },
)(RoutesListCard);



// WEBPACK FOOTER //
// ./src/scripts/components/RoutesListCard/index.jsx